import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  Shield,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Ban,
  TrendingUp,
  AlertCircle
} from 'lucide-react';

const Step5Enhanced = ({ formData, setFormData, onNext, onPrev }) => {
  const [errors, setErrors] = useState({});
  const [agreements, setAgreements] = useState({
    performanceBased: formData.agreePerformanceBased || false,
    noSpam: formData.agreeNoSpam || false,
    followRules: formData.agreeFollowRules || false,
    acceptConsequences: formData.agreeAcceptConsequences || false
  });

  const complianceTerms = [
    {
      id: 'performanceBased',
      icon: TrendingUp,
      title: 'Performance-Based Earnings',
      description: 'I understand that my earnings are based solely on actual sales and conversions. There are no guaranteed earnings or fixed payments.',
      color: 'blue',
      importance: 'critical'
    },
    {
      id: 'noSpam',
      icon: Ban,
      title: 'No Spam or Misleading Promotions',
      description: 'I will not engage in spam, false advertising, misleading claims, or any deceptive promotion practices. All promotions must be honest and transparent.',
      color: 'red',
      importance: 'critical'
    },
    {
      id: 'followRules',
      icon: FileText,
      title: 'Campaign Rules Compliance',
      description: 'I agree to follow all campaign-specific rules, guidelines, and requirements provided for each deal I promote.',
      color: 'purple',
      importance: 'critical'
    },
    {
      id: 'acceptConsequences',
      icon: AlertTriangle,
      title: 'Accept Violations Consequences',
      description: 'I understand that violations of these terms may result in account suspension, earnings forfeiture, or permanent removal from the network.',
      color: 'orange',
      importance: 'critical'
    }
  ];

  const handleAgreementChange = (agreementId) => {
    setAgreements(prev => ({
      ...prev,
      [agreementId]: !prev[agreementId]
    }));

    // Clear error when user checks a box
    if (errors[agreementId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[agreementId];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // All agreements must be checked
    Object.keys(agreements).forEach(key => {
      if (!agreements[key]) {
        newErrors[key] = 'This agreement is mandatory';
      }
    });

    if (Object.keys(newErrors).length > 0) {
      newErrors.general = 'You must agree to all terms to proceed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setFormData(prev => ({
        ...prev,
        agreePerformanceBased: agreements.performanceBased,
        agreeNoSpam: agreements.noSpam,
        agreeFollowRules: agreements.followRules,
        agreeAcceptConsequences: agreements.acceptConsequences,
        complianceAgreedAt: new Date().toISOString()
      }));
      onNext();
    }
  };

  const allAgreed = Object.values(agreements).every(val => val === true);
  const agreementCount = Object.values(agreements).filter(val => val === true).length;
  const totalAgreements = Object.keys(agreements).length;

  const getColorClasses = (color, checked) => {
    const colors = {
      blue: {
        border: checked ? 'border-blue-500' : 'border-gray-300',
        bg: checked ? 'bg-blue-50' : 'bg-white',
        icon: checked ? 'text-blue-600' : 'text-gray-400',
        text: checked ? 'text-blue-700' : 'text-gray-900',
        checkbox: 'text-blue-600'
      },
      red: {
        border: checked ? 'border-red-500' : 'border-gray-300',
        bg: checked ? 'bg-red-50' : 'bg-white',
        icon: checked ? 'text-red-600' : 'text-gray-400',
        text: checked ? 'text-red-700' : 'text-gray-900',
        checkbox: 'text-red-600'
      },
      purple: {
        border: checked ? 'border-purple-500' : 'border-gray-300',
        bg: checked ? 'bg-purple-50' : 'bg-white',
        icon: checked ? 'text-purple-600' : 'text-gray-400',
        text: checked ? 'text-purple-700' : 'text-gray-900',
        checkbox: 'text-purple-600'
      },
      orange: {
        border: checked ? 'border-orange-500' : 'border-gray-300',
        bg: checked ? 'bg-orange-50' : 'bg-white',
        icon: checked ? 'text-orange-600' : 'text-gray-400',
        text: checked ? 'text-orange-700' : 'text-gray-900',
        checkbox: 'text-orange-600'
      }
    };
    return colors[color];
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5 }}
      className="py-4 mx-auto w-full max-w-5xl"
    >
      {/* Header */}
      <div className="mb-6">
        <h2 className="mb-2 text-2xl font-bold text-gray-900">
          E. COMPLIANCE & DECLARATION
          <span className="ml-2 text-red-600">(MANDATORY)</span>
        </h2>
        <p className="text-gray-600">
          Review and agree to our affiliate network policies
        </p>
      </div>

      {/* Critical Warning */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-4 mb-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border-2 border-red-300"
      >
        <div className="flex items-start space-x-3">
          <AlertCircle className="flex-shrink-0 w-6 h-6 mt-0.5 text-red-600" />
          <div className="flex-1">
            <h3 className="mb-2 text-sm font-bold text-red-900">
              ⚠️ IMPORTANT: No Agreement = No Approval
            </h3>
            <p className="text-xs text-red-800">
              You must explicitly agree to all terms below to proceed. These policies protect both you 
              and our merchant partners, ensuring a fair and sustainable affiliate ecosystem.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Progress Indicator */}
      <div className="p-4 mb-6 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            Agreements Progress
          </span>
          <span className={`text-sm font-semibold ${
            allAgreed ? 'text-green-600' : 'text-gray-600'
          }`}>
            {agreementCount} / {totalAgreements}
          </span>
        </div>
        <div className="overflow-hidden w-full h-2 bg-gray-200 rounded-full">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(agreementCount / totalAgreements) * 100}%` }}
            transition={{ duration: 0.3 }}
            className={`h-full rounded-full ${
              allAgreed ? 'bg-green-500' : 'bg-red-500'
            }`}
          />
        </div>
      </div>

      {errors.general && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 mb-6 bg-red-50 rounded-lg border border-red-200"
        >
          <p className="text-sm font-medium text-red-600">{errors.general}</p>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {complianceTerms.map((term, index) => {
          const IconComponent = term.icon;
          const isChecked = agreements[term.id];
          const colorClasses = getColorClasses(term.color, isChecked);
          
          return (
            <motion.div
              key={term.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <label
                className={`flex items-start p-5 rounded-lg border-2 cursor-pointer transition-all duration-200 ${colorClasses.border} ${colorClasses.bg} hover:shadow-md`}
              >
                <div className="flex items-start flex-1 space-x-4">
                  {/* Icon */}
                  <div className={`flex-shrink-0 p-3 rounded-lg ${
                    isChecked 
                      ? `bg-${term.color}-100` 
                      : 'bg-gray-100'
                  }`}>
                    <IconComponent className={`w-6 h-6 ${colorClasses.icon}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className={`text-base font-semibold ${colorClasses.text}`}>
                        {term.title}
                      </h4>
                      {term.importance === 'critical' && (
                        <span className="flex-shrink-0 px-2 py-0.5 ml-2 text-xs font-semibold text-red-600 bg-red-100 rounded-full">
                          CRITICAL
                        </span>
                      )}
                    </div>
                    <p className="text-sm leading-relaxed text-gray-700">
                      {term.description}
                    </p>
                  </div>

                  {/* Checkbox */}
                  <div className="flex-shrink-0 ml-4">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleAgreementChange(term.id)}
                      className={`w-6 h-6 rounded border-gray-300 ${colorClasses.checkbox} focus:ring-2 focus:ring-offset-2 focus:ring-${term.color}-500`}
                    />
                  </div>
                </div>
              </label>

              {errors[term.id] && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 ml-16 text-sm text-red-500"
                >
                  {errors[term.id]}
                </motion.p>
              )}
            </motion.div>
          );
        })}
      </form>

      {/* Summary Box */}
      {allAgreed && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-5 mt-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-300"
        >
          <div className="flex items-start space-x-3">
            <CheckCircle2 className="flex-shrink-0 w-6 h-6 mt-0.5 text-green-600" />
            <div>
              <h3 className="mb-2 text-base font-bold text-green-900">
                ✓ All Agreements Completed
              </h3>
              <p className="text-sm text-green-800">
                Thank you for reviewing and agreeing to our policies. You're ready to proceed to the final step!
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Additional Information */}
      <div className="p-5 mt-6 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start space-x-3">
          <Shield className="flex-shrink-0 w-5 h-5 mt-0.5 text-blue-600" />
          <div>
            <h4 className="mb-2 text-sm font-semibold text-blue-900">
              Your Rights & Support
            </h4>
            <ul className="space-y-1 text-xs text-blue-800">
              <li>• You'll receive full training and support materials after approval</li>
              <li>• Access to our affiliate dashboard with real-time tracking</li>
              <li>• Dedicated support team to help you succeed</li>
              <li>• Fair dispute resolution process for any issues</li>
              <li>• Transparent payment terms and conditions</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8">
        <motion.button
          onClick={onPrev}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center px-6 py-3 space-x-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </motion.button>

        <motion.button
          onClick={handleSubmit}
          whileHover={{ scale: allAgreed ? 1.02 : 1 }}
          whileTap={{ scale: allAgreed ? 0.98 : 1 }}
          disabled={!allAgreed}
          className={`px-8 py-3 font-medium text-white rounded-lg shadow-lg transition-all duration-200 ${
            allAgreed
              ? 'bg-gradient-to-r from-red-500 to-red-600 shadow-red-500/25 hover:from-red-600 hover:to-red-700 hover:shadow-xl hover:shadow-red-500/40 cursor-pointer'
              : 'bg-gray-400 cursor-not-allowed opacity-60'
          }`}
        >
          {allAgreed ? 'Continue to Final Step' : 'Agree to All Terms to Continue'}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default Step5Enhanced;
